/* Live PDF page rendering for the full-screen viewer.

   The old viewer rendered ONE fixed-resolution bitmap and let the browser
   scale it up as you zoomed — so deep zoom went soft and thin lines dissolved.
   Here the page is parsed once and re-rasterised from the vector source at
   whatever resolution the current zoom needs, so it stays crisp all the way in
   (a real PDF viewer, not an upscaled JPEG). pdf.js is loaded on demand. */

type Doc = { numPages: number; getPage: (n: number) => Promise<any>; destroy: () => Promise<void> };

let pdfjsP: Promise<any> | null = null;
async function pdfjs() {
  if (!pdfjsP) pdfjsP = (async () => {
    const lib = await import("pdfjs-dist");
    const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
    lib.GlobalWorkerOptions.workerSrc = worker.default;
    return lib;
  })();
  return pdfjsP;
}

/* Bytes of an original, cached so re-rendering on zoom (or reopening) doesn't
   re-download. Bounded — a big set's originals are large. pdf.js detaches the
   buffer it's handed, so callers always render off a clone. */
const bufCache = new Map<string, Promise<ArrayBuffer>>();
const BUF_MAX = 4;
function bytesOf(url: string): Promise<ArrayBuffer> {
  let p = bufCache.get(url);
  if (!p) {
    p = fetch(url, { credentials: "same-origin" }).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); });
    bufCache.set(url, p);
    if (bufCache.size > BUF_MAX) { const k = bufCache.keys().next().value as string; bufCache.delete(k); }
  }
  return p;
}

export interface LiveSheet {
  aspect: number;                                           // width / height of the page
  render: (canvas: HTMLCanvasElement, backingW: number) => Promise<void>;
  destroy: () => void;
}

/** Open one page of a PDF and keep it parsed so it can be re-rendered at any
    resolution. Call destroy() when the sheet changes or the viewer closes. */
export async function openSheet(url: string, page = 1): Promise<LiveSheet> {
  const lib = await pdfjs();
  const data = (await bytesOf(url)).slice(0);              // clone — getDocument detaches it
  const doc = (await lib.getDocument({ data }).promise) as unknown as Doc;
  const pg = await doc.getPage(Math.min(Math.max(1, page), doc.numPages));
  const base = pg.getViewport({ scale: 1 });
  let task: any = null;
  let dead = false;
  return {
    aspect: base.width / base.height,
    async render(canvas, backingW) {
      if (dead) return;
      const scale = Math.max(0.1, backingW / base.width);
      const vp = pg.getViewport({ scale });
      canvas.width = Math.round(vp.width);
      canvas.height = Math.round(vp.height);
      const ctx = canvas.getContext("2d", { alpha: false })!;
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
      try { task?.cancel(); } catch { /* none */ }
      task = pg.render({ canvasContext: ctx, viewport: vp, intent: "print" });
      await task.promise;
    },
    destroy() {
      dead = true;
      try { task?.cancel(); } catch { /* none */ }
      try { pg.cleanup?.(); } catch { /* gone */ }
      doc.destroy().catch(() => {});
    },
  };
}

/* ---- background prefetch: fit-resolution bitmaps, so opening is instant ---- */

/** Fit-quality crisp bitmaps keyed by url+page. Big enough to look sharp at the
    fitted view; the live canvas takes over for deep zoom. Bounded. */
export const fitCache = new Map<string, string>();
const FIT_MAX = 40;
const FIT_W = 2200;

function fitKey(url: string, page = 1) { return `${url}:${page}`; }
export function fitFor(url?: string, page = 1): string | undefined {
  return url ? fitCache.get(fitKey(url, page)) : undefined;
}

/** Render (once) a fit-quality bitmap for a sheet and cache it. Cheap to call
    repeatedly — a cache hit returns immediately. Tears the doc down after. */
export async function prefetchSheet(url: string, page = 1): Promise<void> {
  const key = fitKey(url, page);
  if (fitCache.has(key)) return;
  try {
    const lib = await pdfjs();
    const data = (await bytesOf(url)).slice(0);
    const doc = (await lib.getDocument({ data }).promise) as unknown as Doc;
    try {
      const pg = await doc.getPage(Math.min(Math.max(1, page), doc.numPages));
      const base = pg.getViewport({ scale: 1 });
      const vp = pg.getViewport({ scale: Math.min(FIT_W / base.width, 5) });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(vp.width); canvas.height = Math.round(vp.height);
      const ctx = canvas.getContext("2d", { alpha: false })!;
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
      const task = pg.render({ canvasContext: ctx, viewport: vp, intent: "print" });
      await Promise.race([task.promise, new Promise((_, rej) => setTimeout(() => { try { task.cancel(); } catch { /* */ } rej(new Error("timeout")); }, 30_000))]);
      const uri = canvas.toDataURL("image/jpeg", 0.9);
      canvas.width = 0; canvas.height = 0;
      if (fitCache.size >= FIT_MAX) { const k = fitCache.keys().next().value as string; fitCache.delete(k); }
      fitCache.set(key, uri);
      try { pg.cleanup?.(); } catch { /* */ }
    } finally { doc.destroy().catch(() => {}); }
  } catch { /* prefetch is best-effort */ }
}

/* A polite background queue: only PDFs, two at a time, yielding to idle time so
   scanning a set never janks the UI. Callers just hand it the sheets. */
let queue: { url: string; page: number }[] = [];
let active = 0;
const CONCURRENCY = 2;

const idle: (cb: () => void) => void =
  typeof (globalThis as any).requestIdleCallback === "function"
    ? (cb) => (globalThis as any).requestIdleCallback(cb, { timeout: 1500 })
    : (cb) => setTimeout(cb, 300);

function pump() {
  while (active < CONCURRENCY && queue.length) {
    const job = queue.shift()!;
    active++;
    prefetchSheet(job.url, job.page).finally(() => { active--; idle(pump); });
  }
}

/** Warm the fit-cache for a set of sheets in the background. Skips ones already
    cached or already queued; safe to call on every render. */
export function prefetchSheets(sheets: { fileUrl?: string; fileName?: string; page?: number }[]) {
  for (const s of sheets) {
    if (!s.fileUrl || !/\.pdf(\?|$)/i.test(s.fileName || s.fileUrl)) continue;
    const page = s.page ?? 1;
    if (fitCache.has(fitKey(s.fileUrl, page))) continue;
    if (queue.some((q) => q.url === s.fileUrl && q.page === page)) continue;
    queue.push({ url: s.fileUrl, page });
  }
  if (queue.length > 60) queue = queue.slice(0, 60);       // never hoard
  idle(pump);
}

/* ---- rasterising an uploaded board set, for a presentation ----
   The architect's boards ARE the concept. To present them we need each page as
   an image: a small one to choose from, and a large one to show the client. */

export interface RasterPage { page: number; thumb: string; aspect: number }

async function renderPageToDataUri(pg: any, targetW: number, quality: number): Promise<{ uri: string; aspect: number }> {
  const base = pg.getViewport({ scale: 1 });
  const scale = Math.max(0.05, targetW / base.width);
  const vp = pg.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(vp.width));
  canvas.height = Math.max(1, Math.round(vp.height));
  const ctx = canvas.getContext("2d", { alpha: false })!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await pg.render({ canvasContext: ctx, viewport: vp }).promise;
  return { uri: canvas.toDataURL("image/jpeg", quality), aspect: base.width / base.height };
}

/** Thumbnails of every page of a File, so the architect can pick the boards. */
export async function rasterisePages(file: File, thumbWidth = 420): Promise<RasterPage[]> {
  const lib = await pdfjs();
  const buf = await file.arrayBuffer();
  const doc: Doc = await lib.getDocument({ data: new Uint8Array(buf) }).promise;
  const out: RasterPage[] = [];
  try {
    for (let n = 1; n <= doc.numPages; n++) {
      const pg = await doc.getPage(n);
      const { uri, aspect } = await renderPageToDataUri(pg, thumbWidth, 0.72);
      out.push({ page: n, thumb: uri, aspect });
      pg.cleanup?.();
    }
  } finally { await doc.destroy().catch(() => {}); }
  return out;
}

/** One page at presentation resolution. Large-format boards need the width. */
export async function renderPageAt(file: File, page: number, width = 2000, quality = 0.86): Promise<string> {
  const lib = await pdfjs();
  const buf = await file.arrayBuffer();
  const doc: Doc = await lib.getDocument({ data: new Uint8Array(buf) }).promise;
  try {
    const pg = await doc.getPage(Math.min(Math.max(1, page), doc.numPages));
    const { uri } = await renderPageToDataUri(pg, width, quality);
    return uri;
  } finally { await doc.destroy().catch(() => {}); }
}
